import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Send, DollarSign, XCircle, Trash2, ExternalLink, RefreshCcw,
  ShieldCheck, Copy, Ban, Mail, ChevronDown, ChevronRight, AlertTriangle,
  Loader2, X as XIcon, BadgeCheck, Undo2, Upload,
} from "lucide-react";
import { api, extractError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

// Status → tailwind pill classes. Kept small so both list + drawer share.
const STATUS_META = {
  draft:    { label: "Draft",    classes: "bg-slate-200 text-slate-800" },
  sent:     { label: "Sent",     classes: "bg-blue-100 text-blue-800" },
  credited: { label: "Credited", classes: "bg-emerald-100 text-emerald-800" },
  denied:   { label: "Denied",   classes: "bg-rose-100 text-rose-800" },
  expired:  { label: "Expired",  classes: "bg-amber-100 text-amber-800" },
};
const TYPE_META = {
  warranty:  { label: "Warranty",  classes: "bg-violet-100 text-violet-800", Icon: ShieldCheck },
  duplicate: { label: "Duplicate", classes: "bg-orange-100 text-orange-800", Icon: Copy },
  // Phase 1 Reject — rose tone matches the REJECTED pill on the Equipment
  // Repairs list + invoice detail card so operators recognise the flow.
  reject:    { label: "Reject",    classes: "bg-rose-100 text-rose-800",    Icon: Ban },
};

// Human-readable labels for the reject reason enum. Kept in sync with the
// backend `RejectReason` values and the picker overlays.
const REJECT_REASON_LABELS = {
  wrong_company:  "Wrong Company",
  equipment_sold: "Equipment Sold",
  other:          "Other",
};
const rejectReasonLabel = (v) => REJECT_REASON_LABELS[v] || (v ? String(v) : null);

const fmtMoney = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return d.toLocaleDateString(undefined,
      { year: "numeric", month: "short", day: "numeric" });
  } catch { return String(v); }
}
const fmtDT    = (v) => { if (!v) return "—"; const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v).slice(0,16) : d.toLocaleString(undefined, {dateStyle:"medium", timeStyle:"short"}); };

export default function ShopCredits() {
  const { user, hasPerm, loading: authLoading } = useAuth();
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [claimType, setClaimType] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (claimType !== "all") params.claim_type = claimType;
      if (search) params.invoice_number = search;
      const { data } = await api.get("/shop-credits", { params });
      setList(data.items || []);
      setCounts(data.counts_by_status || {});
    } catch (e) {
      toast.error(extractError(e, "Failed to load claims"));
    } finally {
      setLoading(false);
    }
  }, [status, claimType, search]);

  useEffect(() => { if (!authLoading && user) load(); }, [authLoading, user, load]);

  if (authLoading) return null;
  if (!user || !hasPerm("nav.shop_credits")) return <Navigate to="/" replace />;

  const StatusPill = ({ v }) => {
    const m = STATUS_META[v] || { label: v || "?", classes: "bg-slate-100 text-slate-700" };
    return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${m.classes}`} data-testid={`sc-status-${v}`}>{m.label}</span>;
  };

  return (
    <div className="space-y-4 pb-10" data-testid="shop-credits-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Shop Credits</h1>
          <p className="text-sm text-slate-500">
            Draft claims auto-created from Warranty or Duplicate correction allocations.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="sc-refresh-btn">
          <RefreshCcw className="mr-1 h-4 w-4" /> Refresh
        </Button>
      </header>

      {/* Status summary strip */}
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(STATUS_META).map(([key, meta]) => {
          const c = counts[key] || { count: 0, amount: 0 };
          return (
            <span key={key} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${meta.classes}`}>
              <span className="font-semibold">{c.count}</span> {meta.label}
              <span className="opacity-70">· {fmtMoney(c.amount)}</span>
            </span>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-40">
          <label className="mb-1 block text-xs font-medium text-slate-600">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="sc-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <label className="mb-1 block text-xs font-medium text-slate-600">Type</label>
          <Select value={claimType} onValueChange={setClaimType}>
            <SelectTrigger data-testid="sc-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="warranty">Warranty</SelectItem>
              <SelectItem value="duplicate">Duplicate</SelectItem>
              <SelectItem value="reject">Reject</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[220px] flex-1">
          <label className="mb-1 block text-xs font-medium text-slate-600">Invoice number</label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. 18426" data-testid="sc-filter-invoice" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border border-slate-200 bg-white">
        <Table data-testid="shop-credits-table">
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Ref</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Shop</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : list.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-slate-500">
                No shop credit claims yet. They're auto-created when you flag a repair as Warranty, Duplicate, or Reject.
              </TableCell></TableRow>
            ) : list.map((c) => {
              const T = TYPE_META[c.claim_type];
              return (
                <TableRow
                  key={c.id}
                  onClick={() => setOpenId(c.id)}
                  className="cursor-pointer hover:bg-slate-50"
                  data-testid={`sc-row-${c.claim_reference}`}
                >
                  <TableCell className="font-mono text-xs">{c.claim_reference}</TableCell>
                  <TableCell>
                    {T && (
                      <div className="flex flex-col gap-0.5">
                        <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${T.classes}`}>
                          <T.Icon className="h-3 w-3" /> {T.label}
                        </span>
                        {c.claim_type === "reject" && rejectReasonLabel(c.reject_reason) && (
                          <span
                            className="text-[11px] text-slate-600"
                            data-testid={`sc-row-reject-reason-${c.claim_reference}`}
                          >
                            {rejectReasonLabel(c.reject_reason)}
                          </span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[220px] truncate text-sm">{c.shop_name || "—"}</TableCell>
                  <TableCell className="text-sm">#{c.invoice_number}</TableCell>
                  <TableCell className="text-sm">{c.unit_number} <span className="text-xs text-slate-500">({(c.unit_type || "").slice(0,1).toUpperCase()})</span></TableCell>
                  <TableCell className="text-right font-medium">{fmtMoney(c.claim_amount)}</TableCell>
                  <TableCell><StatusPill v={c.status} /></TableCell>
                  <TableCell className="text-xs text-slate-600">{fmtDate(c.created_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {openId && (
        <ClaimDrawer
          claimId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Detail drawer — draft edit + state transitions.
// ---------------------------------------------------------------------------

function ClaimDrawer({ claimId, onClose, onChanged }) {
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});
  const [creditOpen, setCreditOpen] = useState(false);
  const [uncreditOpen, setUncreditOpen] = useState(false);
  const [denyOpen, setDenyOpen] = useState(false);
  // Phase B — SMTP-driven Send-by-Email flow. Replaces the old "download
  // stored PDF" hack that piggy-backed on the pre-Phase-A /send endpoint.
  const [sendEmailOpen, setSendEmailOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/shop-credits/${claimId}`);
      setClaim(data);
      setDraft({
        shop_name: data.shop_name || "",
        shop_contact_email: data.shop_contact_email || "",
        shop_contact_phone: data.shop_contact_phone || "",
        shop_address: data.shop_address || "",
        operator_note: data.operator_note || "",
      });
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => { reload(); }, [reload]);

  const isDraft = claim?.status === "draft";
  const dirty = useMemo(() => {
    if (!claim) return false;
    return ["shop_name","shop_contact_email","shop_contact_phone","shop_address","operator_note"]
      .some((k) => (draft[k] || "") !== (claim[k] || ""));
  }, [claim, draft]);

  const savePatch = async () => {
    setSaving(true);
    try {
      await api.patch(`/shop-credits/${claimId}`, draft);
      toast.success("Draft saved");
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const previewPdf = () => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const token = localStorage.getItem("token") || localStorage.getItem("access_token");
    // Preview opens in a new tab; token is embedded via axios interceptor won't work for direct window.open.
    // Fall back to fetching bytes and opening as blob URL for guaranteed auth.
    api.get(`/shop-credits/${claimId}/pdf`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch((e) => toast.error(extractError(e, "Preview failed")));
  };

  const sendClaimEmailSuccess = () => {
    // Phase B — after a successful SMTP send, refresh drawer + list badges.
    window.dispatchEvent(new Event("shop-credits:refresh-badge"));
    reload();
    onChanged?.();
  };

  const downloadStored = async () => {
    try {
      const r = await api.get(`/shop-credits/${claimId}/document`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${claim.claim_reference}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(extractError(e)); }
  };

  const deleteDraft = async () => {
    if (!window.confirm("Delete this draft claim? This cannot be undone.")) return;
    setSaving(true);
    try {
      await api.delete(`/shop-credits/${claimId}`);
      toast.success("Draft deleted");
      window.dispatchEvent(new Event("shop-credits:refresh-badge"));
      onClose();
      onChanged?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally { setSaving(false); }
  };

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl" data-testid="sc-drawer">
        {loading || !claim ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            <SheetHeader>
              <div className="flex items-center justify-between gap-2">
                <SheetTitle className="font-mono text-lg" data-testid="sc-drawer-ref">{claim.claim_reference}</SheetTitle>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${(STATUS_META[claim.status]||{}).classes || "bg-slate-100"}`}>
                  {(STATUS_META[claim.status]||{}).label || claim.status}
                </span>
              </div>
            </SheetHeader>

            <div className="mt-4 space-y-6 text-sm">
              {/* Invoice + unit summary — read-only */}
              <section className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Source</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <Field label="Type">
                    {(() => {
                      const T = TYPE_META[claim.claim_type];
                      return T ? <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${T.classes}`}><T.Icon className="h-3 w-3" /> {T.label}</span> : claim.claim_type;
                    })()}
                  </Field>
                  <Field label="Invoice">#{claim.invoice_number}</Field>
                  <Field label="Service date">{fmtDate(claim.service_date)}</Field>
                  <Field label="Unit">{claim.unit_number} ({claim.unit_type})</Field>
                  <Field label="Claim amount"><span className="font-semibold text-slate-900">{fmtMoney(claim.claim_amount)}</span></Field>
                  <Field label="Corrections">{(claim.corrections || []).length}</Field>
                </div>
                {claim.claim_type === "warranty" && (claim.prior_invoice_numbers || []).length > 0 && (
                  <div className="mt-2 text-xs text-slate-600">
                    Prior invoices: {claim.prior_invoice_numbers.map((p) => `#${p}`).join(", ")}
                  </div>
                )}
                {claim.claim_type === "reject" && (
                  <div className="mt-2 space-y-1 border-t border-slate-200 pt-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Reject details
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <Field label="Reason">
                        <span data-testid="sc-drawer-reject-reason">
                          {rejectReasonLabel(claim.reject_reason) || "—"}
                        </span>
                      </Field>
                    </div>
                    {claim.reject_comment && (
                      <div>
                        <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">Comment</div>
                        <div
                          className="whitespace-pre-wrap text-sm text-slate-700"
                          data-testid="sc-drawer-reject-comment"
                        >
                          {claim.reject_comment}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Corrections list */}
              <section>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Corrections</div>
                <div className="rounded border border-slate-200">
                  <table className="w-full text-sm">
                    <tbody>
                      {(claim.corrections || []).map((c) => (
                        <tr key={c.id} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-2 align-top">
                            <div className="line-clamp-2 max-w-[380px] text-slate-700">{c.description || c.complaint_text || "—"}</div>
                            <div className="text-[11px] text-slate-500">{[c.global_component, c.global_system].filter(Boolean).join(" · ")}</div>
                          </td>
                          <td className="w-24 px-3 py-2 text-right font-medium">{fmtMoney(c.amount)}</td>
                        </tr>
                      ))}
                      {(!claim.corrections || claim.corrections.length === 0) && (
                        <tr><td className="px-3 py-4 text-center text-slate-500" colSpan={2}>No corrections linked.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Editable shop info + note (draft only) */}
              <section className="space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Shop contact</div>
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput label="Shop name" value={draft.shop_name} onChange={(v) => setDraft((d) => ({...d, shop_name: v}))} disabled={!isDraft} testid="sc-input-shop-name" />
                  <LabeledInput label="Contact email" value={draft.shop_contact_email} onChange={(v) => setDraft((d) => ({...d, shop_contact_email: v}))} disabled={!isDraft} testid="sc-input-shop-email" />
                  <LabeledInput label="Contact phone" value={draft.shop_contact_phone} onChange={(v) => setDraft((d) => ({...d, shop_contact_phone: v}))} disabled={!isDraft} testid="sc-input-shop-phone" />
                  <LabeledInput label="Address" value={draft.shop_address} onChange={(v) => setDraft((d) => ({...d, shop_address: v}))} disabled={!isDraft} testid="sc-input-shop-address" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Operator note</label>
                  <Textarea
                    value={draft.operator_note}
                    onChange={(e) => setDraft((d) => ({...d, operator_note: e.target.value}))}
                    disabled={!isDraft}
                    placeholder="Notes to include in the PDF for the shop"
                    className="min-h-[80px]"
                    data-testid="sc-input-operator-note"
                  />
                </div>
              </section>

              {/* Phase B — Credit Details. Only when the claim is
                  credited. Shows the whole credit record + a download
                  link for the attached memo if present. */}
              {claim.status === "credited" && (
                <CreditDetailsSection claim={claim} onNavigate={(path) => navigate(path)} />
              )}

              {/* Phase B — Credit History. Independent of current status;
                  renders any credit_events[] (mark + reverse cycles). */}
              {(claim.credit_events || []).length > 0 && (
                <CreditHistorySection events={claim.credit_events} />
              )}

              {/* Phase B — Send history. Only rendered when there's at
                  least one send_event. Reads directly from claim state,
                  no API round-trip. */}
              {(claim.send_events || []).length > 0 && (
                <SendHistorySection events={claim.send_events} />
              )}

              {/* Timeline */}
              <section>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Timeline</div>
                <ol className="space-y-1 text-xs text-slate-600">
                  <TimelineRow label="Created" who={claim.created_by} at={claim.created_at} />
                  {claim.sent_at && <TimelineRow label="Sent" who={claim.sent_by} at={claim.sent_at} />}
                  {claim.credited_at && <TimelineRow label={`Credited · ${fmtMoney(claim.credited_amount)}${claim.credit_reference ? ` · ref ${claim.credit_reference}` : ""}`} who={claim.credited_by} at={claim.credited_at} />}
                  {claim.denied_at && <TimelineRow label={`Denied · ${claim.denied_reason || "no reason"}`} who={claim.denied_by} at={claim.denied_at} />}
                </ol>
              </section>
            </div>

            {/* Action bar */}
            <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 pt-4">
              {isDraft && (
                <>
                  <Button variant="outline" size="sm" onClick={deleteDraft} disabled={saving} data-testid="sc-btn-delete">
                    <Trash2 className="mr-1 h-4 w-4" /> Delete draft
                  </Button>
                  <Button variant="outline" size="sm" onClick={savePatch} disabled={saving || !dirty} data-testid="sc-btn-save">
                    Save changes
                  </Button>
                  <Button variant="outline" size="sm" onClick={previewPdf} disabled={saving} data-testid="sc-btn-preview">
                    <FileText className="mr-1 h-4 w-4" /> Preview PDF
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setSendEmailOpen(true)}
                    disabled={saving || dirty}
                    title={dirty ? "Save changes first" : "Preview and send this claim by email"}
                    data-testid="sc-btn-send-email"
                  >
                    <Mail className="mr-1 h-4 w-4" /> Send by Email
                  </Button>
                </>
              )}
              {claim.status === "sent" && (
                <>
                  <Button variant="outline" size="sm" onClick={downloadStored} data-testid="sc-btn-download">
                    <FileText className="mr-1 h-4 w-4" /> Download original PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSendEmailOpen(true)}
                    data-testid="sc-btn-send-email"
                    title="Re-send the email to the shop"
                  >
                    <Mail className="mr-1 h-4 w-4" /> Send by Email
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setDenyOpen(true)} data-testid="sc-btn-deny">
                    <XCircle className="mr-1 h-4 w-4" /> Mark denied
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setCreditOpen(true)}
                    data-testid="sc-btn-credit"
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    <BadgeCheck className="mr-1 h-4 w-4" /> Mark Credited
                  </Button>
                </>
              )}
              {claim.status === "credited" && (
                <>
                  {claim.sent_document_id && (
                    <Button variant="outline" size="sm" onClick={downloadStored} data-testid="sc-btn-download">
                      <FileText className="mr-1 h-4 w-4" /> Download PDF
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSendEmailOpen(true)}
                    data-testid="sc-btn-send-email"
                    title="Re-send the email (e.g. resend the credit confirmation copy)"
                  >
                    <Mail className="mr-1 h-4 w-4" /> Send by Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUncreditOpen(true)}
                    data-testid="sc-btn-uncredit"
                    className="border-amber-300 text-amber-800 hover:bg-amber-50"
                    title="Reverse the credit — moves the claim back to Sent"
                  >
                    <Undo2 className="mr-1 h-4 w-4" /> Undo Credit
                  </Button>
                </>
              )}
              {claim.status === "denied" && claim.sent_document_id && (
                <Button variant="outline" size="sm" onClick={downloadStored} data-testid="sc-btn-download">
                  <FileText className="mr-1 h-4 w-4" /> Download PDF
                </Button>
              )}
            </div>

            <MarkCreditedDialog open={creditOpen} onOpenChange={setCreditOpen} claim={claim} onSaved={() => { reload(); onChanged?.(); }} navigate={navigate} />
            <UndoCreditDialog open={uncreditOpen} onOpenChange={setUncreditOpen} claim={claim} onSaved={() => { reload(); onChanged?.(); }} />
            <MarkDeniedDialog open={denyOpen} onOpenChange={setDenyOpen} claim={claim} onSaved={() => { reload(); onChanged?.(); }} />
            {/* Send-by-Email dialog — visibility gated by the parent state
                so the dialog is only mounted when opened. Backend blocks
                voided/denied/expired with 409, but we also hide the button
                for those states so the modal never opens on them. */}
            {sendEmailOpen && (
              <SendByEmailDialog
                claim={claim}
                onClose={() => setSendEmailOpen(false)}
                onSent={sendClaimEmailSuccess}
              />
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-slate-800">{children}</div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid} />
    </div>
  );
}

function TimelineRow({ label, who, at }) {
  // `who` may be a string (legacy) or `{user_id, email}` (Phase A/B). Coerce.
  const whoDisplay = who && typeof who === "object" ? (who.email || who.user_id || "—") : (who || "—");
  return (
    <li className="flex flex-wrap items-baseline gap-2">
      <span className="font-medium text-slate-800">{label}</span>
      <span>· {fmtDT(at)}</span>
      <span className="text-slate-500">· by {whoDisplay}</span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// MarkCreditedDialog — Phase B mark-credited workflow.
// ---------------------------------------------------------------------------
const CREDIT_METHODS = [
  {
    value: "shop_reissued_original_invoice",
    label: "Shop reissued original invoice",
    hint:  "The shop updated the invoice in FullBay with the duplicate line removed.",
  },
  {
    value: "credit_memo",
    label: "Credit memo (paperwork)",
    hint:  "The shop issued a separate credit memo; original invoice unchanged.",
  },
  {
    value: "bank_refund",
    label: "Bank / EFS refund received",
    hint:  "The shop refunded to our bank account or EFS.",
  },
];
const CREDIT_METHOD_LABEL = Object.fromEntries(
  CREDIT_METHODS.map((m) => [m.value, m.label]));

const _ALLOWED_DOC_MIME = new Set([
  "application/pdf", "image/jpeg", "image/png",
]);
const _MAX_DOC_BYTES = 10 * 1024 * 1024;

const _fmtBytesLocal = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
};

function _todayISO() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function _detailToMessage(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation error array — collapse to a readable single line.
    return detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
  }
  if (typeof detail === "object") return detail.message || detail.error || fallback;
  return String(detail || fallback);
}


function MarkCreditedDialog({ open, onOpenChange, claim, onSaved, navigate }) {
  const [amount, setAmount] = useState("");
  const [creditedOn, setCreditedOn] = useState(_todayISO());
  const [method, setMethod] = useState("");
  const [shopRef, setShopRef] = useState("");
  const [comment, setComment] = useState("");

  // Doc upload state — kept simple: one attachment per credit.
  const [docState, setDocState] = useState({ status: "idle" });
  // { status: "idle" }
  // { status: "uploading", filename, size }
  // { status: "uploaded", document_id, filename, size, content_type }
  // { status: "error", message }

  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState(null);
  const [blocking, setBlocking] = useState(false);   // 409 → keep Save disabled

  const fileInputRef = useRef(null);

  const claimAmt = Number(claim?.claim_amount || 0);
  const amountNum = Number(amount);
  const delta = Number.isFinite(amountNum) ? +(amountNum - claimAmt).toFixed(2) : 0;
  const trimmedComment = comment.trim();
  const uploading = docState.status === "uploading";
  const uploadHasError = docState.status === "error";

  useEffect(() => {
    if (open) {
      setAmount(String(claim?.claim_amount ?? ""));
      setCreditedOn(_todayISO());
      setMethod(""); setShopRef(""); setComment("");
      setDocState({ status: "idle" });
      setSaving(false); setInlineError(null); setBlocking(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open, claim]);

  const canSubmit = (
    !saving && !blocking &&
    Number.isFinite(amountNum) && amountNum > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(creditedOn) &&
    !!method &&
    trimmedComment.length > 0 &&
    !uploading && !uploadHasError
  );

  const pickFile = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Client-side gates match the backend so users get feedback pre-upload.
    if (!_ALLOWED_DOC_MIME.has(f.type)) {
      setDocState({ status: "error",
        message: `Unsupported file type (${f.type || "unknown"}). PDF, JPG, PNG only.` });
      return;
    }
    if (f.size > _MAX_DOC_BYTES) {
      setDocState({ status: "error",
        message: `File too large (${_fmtBytesLocal(f.size)}). Max 10 MB.` });
      return;
    }
    setDocState({ status: "uploading", filename: f.name, size: f.size });
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(
        `/shop-credits/${claim.id}/credit-document`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setDocState({
        status: "uploaded",
        document_id: data.document_id,
        filename: data.filename,
        size: data.size_bytes,
        content_type: data.content_type,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setDocState({ status: "error",
        message: _detailToMessage(detail, "Upload failed") });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clearFile = () => {
    setDocState({ status: "idle" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true); setInlineError(null);
    try {
      const body = {
        credited_amount: amountNum,
        credited_on:     creditedOn,
        credit_method:   method,
        credit_comment:  trimmedComment,
        confirm:         true,
      };
      if (shopRef.trim()) body.shop_reference = shopRef.trim();
      if (docState.status === "uploaded") body.credit_document_id = docState.document_id;
      const { data } = await api.post(
        `/shop-credits/${claim.id}/mark-credited`, body);
      // Delta-annotated toast.
      const d = Number(data?.credit_delta_from_claim || 0);
      const deltaNote = d === 0 ? ""
        : ` (${d > 0 ? "+" : "−"}$${Math.abs(d).toFixed(2)} delta)`;
      toast.success(
        `Claim marked credited — $${Number(body.credited_amount).toFixed(2)}${deltaNote}`,
      );
      // Reissue nudge — when the method implies FullBay-side changes.
      const ids = data?.suggested_sync_invoice_ids || [];
      if (ids.length && navigate) {
        toast("Shop reissued the invoice — trigger a FullBay sync to pull the updated line items.", {
          duration: 15000,
          action: {
            label: "Sync now",
            onClick: () => navigate("/equipment-repairs"),
          },
        });
      }
      window.dispatchEvent(new Event("shop-credits:refresh-badge"));
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      const status = err?.response?.status;
      const msg = _detailToMessage(err?.response?.data?.detail, "Failed to mark credited");
      if (status === 409) { setInlineError(msg); setBlocking(true); }
      else if (status === 404) toast.error("Claim not found — refresh the page");
      else setInlineError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl" data-testid="sc-credit-dialog">
        <DialogHeader>
          <DialogTitle>
            Mark credited — <span className="font-mono text-base">{claim?.claim_reference}</span>
          </DialogTitle>
          <DialogDescription>
            Record how the shop resolved this claim. All fields are captured on the audit trail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Credited amount *</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-500">$</span>
                <Input
                  type="number" step="0.01" min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-6"
                  data-testid="sc-credit-amount"
                  disabled={saving || blocking}
                />
              </div>
              {Number.isFinite(amountNum) && delta !== 0 && (
                <div
                  className={`mt-1 text-[11px] ${delta > 0 ? "text-emerald-700" : "text-amber-700"}`}
                  data-testid="sc-credit-delta-hint"
                >
                  {delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(2)}{" "}
                  {delta > 0 ? "over claim amount" : "under claim amount"}
                </div>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Credited on *</label>
              <Input
                type="date" value={creditedOn}
                onChange={(e) => setCreditedOn(e.target.value)}
                data-testid="sc-credit-date"
                disabled={saving || blocking}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Credit method *</label>
            <div className="space-y-1.5" role="radiogroup">
              {CREDIT_METHODS.map((m) => (
                <label
                  key={m.value}
                  className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm hover:bg-slate-50 ${
                    method === m.value ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"
                  } ${(saving || blocking) ? "pointer-events-none opacity-60" : ""}`}
                  data-testid={`sc-credit-method-${m.value}`}
                >
                  <input
                    type="radio" name="credit-method" value={m.value}
                    checked={method === m.value}
                    onChange={() => setMethod(m.value)}
                    className="mt-1 h-3.5 w-3.5"
                    disabled={saving || blocking}
                  />
                  <div>
                    <div className="font-medium text-slate-800">{m.label}</div>
                    <div className="text-[11px] text-slate-500">{m.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Shop reference #</label>
            <Input
              placeholder="e.g. CM-8842"
              value={shopRef} onChange={(e) => setShopRef(e.target.value)}
              data-testid="sc-credit-shop-ref"
              disabled={saving || blocking}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Attach document</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={handleFile}
              className="hidden"
              data-testid="sc-credit-file-input"
            />
            {docState.status === "idle" && (
              <Button variant="outline" size="sm" onClick={pickFile} type="button"
                       disabled={saving || blocking} data-testid="sc-credit-upload-btn">
                <Upload className="mr-1 h-4 w-4" /> Choose file (PDF/JPG/PNG, max 10MB)
              </Button>
            )}
            {docState.status === "uploading" && (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Loader2 className="h-3 w-3 animate-spin" />
                Uploading {docState.filename} · {_fmtBytesLocal(docState.size)}…
              </div>
            )}
            {docState.status === "uploaded" && (
              <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs text-emerald-800"
                   data-testid="sc-credit-upload-success">
                <FileText className="h-3.5 w-3.5" />
                <span className="font-medium">{docState.filename}</span>
                <span className="text-emerald-700">· {_fmtBytesLocal(docState.size)}</span>
                <Button variant="ghost" size="sm" onClick={clearFile} type="button"
                         className="ml-auto h-6 px-2 text-xs text-emerald-800 hover:bg-emerald-100"
                         disabled={saving || blocking}
                         data-testid="sc-credit-upload-remove">
                  Remove
                </Button>
              </div>
            )}
            {docState.status === "error" && (
              <div className="flex items-center gap-2">
                <Alert variant="destructive" className="flex-1">
                  <AlertDescription>{docState.message}</AlertDescription>
                </Alert>
                <Button variant="outline" size="sm" onClick={pickFile} type="button"
                         disabled={saving || blocking}>
                  Retry
                </Button>
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Comment / notes *
            </label>
            <Textarea
              rows={2}
              placeholder="Explain any delta from the claim amount, or add notes for the audit trail"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="sc-credit-comment"
              disabled={saving || blocking}
            />
            {comment.length > 0 && trimmedComment.length === 0 && (
              <div className="mt-1 text-[11px] text-rose-600">Comment cannot be blank.</div>
            )}
          </div>

          {inlineError && (
            <Alert variant="destructive" data-testid="sc-credit-error">
              <AlertDescription>{inlineError}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                   disabled={saving} data-testid="sc-credit-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
            data-testid="sc-credit-confirm"
          >
            {saving
              ? (<><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Saving…</>)
              : (<><BadgeCheck className="mr-1 h-4 w-4" /> Mark credited</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// UndoCreditDialog — reverses `credited → sent`. Comment required.
// ---------------------------------------------------------------------------
function UndoCreditDialog({ open, onOpenChange, claim, onSaved }) {
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  useEffect(() => {
    if (open) { setComment(""); setSaving(false); setInlineError(null); }
  }, [open]);

  const canSubmit = !saving && comment.trim().length > 0;

  const submit = async () => {
    setSaving(true); setInlineError(null);
    try {
      await api.post(`/shop-credits/${claim.id}/uncredit`,
        { comment: comment.trim(), confirm: true });
      toast.success("Credit reversed — claim moved back to Sent.");
      window.dispatchEvent(new Event("shop-credits:refresh-badge"));
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      setInlineError(_detailToMessage(err?.response?.data?.detail,
                                       "Failed to undo credit"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent data-testid="sc-uncredit-dialog">
        <DialogHeader>
          <DialogTitle>
            Undo credit on <span className="font-mono text-base">{claim?.claim_reference}</span>?
          </DialogTitle>
          <DialogDescription>
            The claim will move back to <strong>Sent</strong>. Credit history is preserved
            in the audit trail; attached memo (if any) is kept in storage.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Why is this being undone? *
          </label>
          <Textarea
            rows={3}
            placeholder="Shop reversed the credit, wrong amount recorded, etc."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={saving}
            data-testid="sc-uncredit-comment"
          />
          {inlineError && (
            <Alert variant="destructive" data-testid="sc-uncredit-error">
              <AlertDescription>{inlineError}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                   disabled={saving} data-testid="sc-uncredit-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
            data-testid="sc-uncredit-confirm"
          >
            {saving
              ? (<><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Reversing…</>)
              : (<><Undo2 className="mr-1 h-4 w-4" /> Undo Credit</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// CreditDetailsSection — resolution panel shown on credited claims.
// ---------------------------------------------------------------------------
function CreditDetailsSection({ claim, onNavigate }) {
  const [downloading, setDownloading] = useState(false);
  const delta = Number(claim.credit_delta_from_claim || 0);
  const hasDoc = !!claim.credit_document_id;

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/shop-credits/${claim.id}/credit-document`,
                                 { responseType: "blob" });
      const filename = `${claim.claim_reference || "credit-doc"}-credit`
        + (guessExt(r.headers?.["content-type"]) || "");
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section
      className="rounded-md border-l-4 border-emerald-500 bg-emerald-50/40 p-3"
      data-testid="sc-credit-details"
    >
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
          Credit Details
        </div>
        {claim.credited_at && (
          <div className="text-[11px] text-slate-500">
            Recorded {fmtDT(claim.credited_at)}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <Field label="Credited amount">
          <span className="font-medium">
            {fmtMoney(claim.credited_amount)}
            {delta !== 0 && (
              <span
                className={`ml-2 rounded-full px-2 py-0.5 text-[11px] ${
                  delta > 0
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-amber-100 text-amber-800"
                }`}
                data-testid="sc-credit-details-delta"
              >
                {delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(2)}{" "}
                {delta > 0 ? "over" : "under"} claim
              </span>
            )}
          </span>
        </Field>
        <Field label="Credited on">
          {claim.credited_on ? fmtDate(claim.credited_on) : "—"}
        </Field>
        <Field label="Method">
          {CREDIT_METHOD_LABEL[claim.credit_method] || claim.credit_method || "—"}
        </Field>
        <Field label="Shop reference">
          {claim.shop_reference || "—"}
        </Field>
        <Field label="Recorded by">
          {claim.credited_by?.email || (typeof claim.credited_by === "string"
                                          ? claim.credited_by : "—")}
        </Field>
      </div>
      {claim.credit_comment && (
        <div className="mt-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
            Comment
          </div>
          <div className="whitespace-pre-wrap text-sm text-slate-800"
                data-testid="sc-credit-details-comment">
            {claim.credit_comment}
          </div>
        </div>
      )}
      {hasDoc && (
        <div className="mt-2 flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-emerald-700" />
          <span className="text-xs text-slate-700">Attached memo on file.</span>
          <Button variant="outline" size="sm" onClick={download}
                   disabled={downloading}
                   className="ml-auto h-7 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                   data-testid="sc-credit-details-download">
            {downloading
              ? (<><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Downloading…</>)
              : (<><FileText className="mr-1 h-3 w-3" /> Download</>)}
          </Button>
        </div>
      )}
      {claim.credit_method === "shop_reissued_original_invoice" && (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
              data-testid="sc-credit-reissue-notice">
          <div className="flex items-center justify-between gap-2">
            <span>
              Shop reissued the original invoice — trigger a FullBay sync to
              pull the updated line items.
            </span>
            <Button variant="outline" size="sm"
                     onClick={() => onNavigate?.("/equipment-repairs")}
                     className="h-7 border-amber-400 text-amber-900 hover:bg-amber-100"
                     data-testid="sc-credit-reissue-sync">
              Go to Equipment Repairs
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}


function guessExt(ct) {
  const c = String(ct || "").toLowerCase();
  if (c.includes("pdf")) return ".pdf";
  if (c.includes("png")) return ".png";
  if (c.includes("jpeg") || c.includes("jpg")) return ".jpg";
  return "";
}


// ---------------------------------------------------------------------------
// CreditHistorySection — mirrors SendHistorySection pattern.
// ---------------------------------------------------------------------------
const CREDIT_EVENT_LABEL = {
  claim_credited:   "Claim credited",
  credit_reversed:  "Credit reversed",
};

function CreditHistorySection({ events }) {
  const [open, setOpen] = useState(true);
  const sorted = useMemo(
    () => (events || []).slice().sort(
      (a, b) => String(b.at || "").localeCompare(String(a.at || ""))
    ),
    [events],
  );
  return (
    <section data-testid="sc-credit-history">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Credit history ({sorted.length})
      </button>
      {open && (
        <div className="rounded-md border border-slate-200 bg-white">
          {sorted.map((ev, i) => (
            <CreditHistoryRow key={ev.event_id || `${ev.type}-${i}`} ev={ev} />
          ))}
        </div>
      )}
    </section>
  );
}

function CreditHistoryRow({ ev }) {
  const [expanded, setExpanded] = useState(false);
  const label = CREDIT_EVENT_LABEL[ev.type] || ev.type;
  const isReverse = ev.type === "credit_reversed";
  return (
    <div className="border-b border-slate-100 px-3 py-2 text-sm last:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-medium text-slate-800">{fmtDT(ev.at)}</span>
          <span className="text-xs text-slate-500">by {ev.by?.email || "—"}</span>
          <Badge
            className={isReverse
              ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
              : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"}
          >
            {label}
          </Badge>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          {expanded ? "Hide details" : "Details"}
        </button>
      </div>
      {!expanded ? (
        <div className="mt-0.5 text-xs text-slate-600">
          {isReverse
            ? (ev.comment || "—")
            : `${fmtMoney(ev.credited_amount)} · ${CREDIT_METHOD_LABEL[ev.credit_method] || ev.credit_method}${ev.shop_reference ? ` · ref ${ev.shop_reference}` : ""}`}
        </div>
      ) : (
        <div className="mt-2 space-y-1 rounded bg-slate-50 p-2 text-[11px] text-slate-700">
          {!isReverse && (
            <>
              <div><span className="text-slate-500">Amount:</span> {fmtMoney(ev.credited_amount)}</div>
              <div><span className="text-slate-500">Method:</span> {CREDIT_METHOD_LABEL[ev.credit_method] || ev.credit_method}</div>
              {ev.shop_reference && <div><span className="text-slate-500">Shop ref:</span> {ev.shop_reference}</div>}
              {typeof ev.credit_delta_from_claim === "number" && ev.credit_delta_from_claim !== 0 && (
                <div><span className="text-slate-500">Δ vs claim:</span> {ev.credit_delta_from_claim > 0 ? "+" : "−"}${Math.abs(ev.credit_delta_from_claim).toFixed(2)}</div>
              )}
              {ev.has_document && <div><span className="text-slate-500">Attachment:</span> yes</div>}
              {ev.credit_comment && (
                <div className="whitespace-pre-wrap">
                  <span className="text-slate-500">Comment:</span> {ev.credit_comment}
                </div>
              )}
            </>
          )}
          {isReverse && (
            <>
              {ev.comment && <div className="whitespace-pre-wrap"><span className="text-slate-500">Reason:</span> {ev.comment}</div>}
              {ev.prior_credited_amount != null && (
                <div><span className="text-slate-500">Prior amount:</span> {fmtMoney(ev.prior_credited_amount)}</div>
              )}
              {ev.prior_credit_method && (
                <div><span className="text-slate-500">Prior method:</span> {CREDIT_METHOD_LABEL[ev.prior_credit_method] || ev.prior_credit_method}</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


function MarkDeniedDialog({ open, onOpenChange, claim, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/shop-credits/${claim.id}/mark-denied`, { denied_reason: reason });
      toast.success("Claim marked as denied");
      window.dispatchEvent(new Event("shop-credits:refresh-badge"));
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent data-testid="sc-deny-dialog">
        <DialogHeader><DialogTitle>Mark claim as denied</DialogTitle></DialogHeader>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Reason (required)</label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="min-h-[80px]" data-testid="sc-deny-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !reason.trim()} data-testid="sc-deny-confirm">{saving ? "Saving…" : "Confirm"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Phase B — Send-by-Email flow. One dialog + one drawer section.
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
const isValidEmail = (v) => !!v && EMAIL_RE.test(String(v).trim());

const fmtBytes = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
};

/** Compact chip-style multi-value email input.
 *  * Enter / comma / blur commits the current draft as a chip.
 *  * Backspace on empty input removes the last chip.
 *  * Invalid chips render with a rose border but stay in the list so the user
 *    can fix or remove them — the parent gates Submit on `allValid`.
 */
function EmailChipsInput({ value, onChange, placeholder, disabled, testid }) {
  const [draftText, setDraftText] = useState("");
  const inputRef = useRef(null);

  const commit = (raw) => {
    const parts = String(raw)
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const next = [...value];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    onChange(next);
    setDraftText("");
  };

  const removeAt = (i) => {
    const next = value.slice(); next.splice(i, 1); onChange(next);
  };

  return (
    <div
      className={`flex min-h-[38px] flex-wrap items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus-within:border-slate-500 ${disabled ? "opacity-60" : ""}`}
      onClick={() => inputRef.current?.focus()}
      data-testid={testid}
    >
      {value.map((v, i) => {
        const ok = isValidEmail(v);
        return (
          <span
            key={`${v}-${i}`}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${ok ? "bg-slate-100 text-slate-800" : "border border-rose-300 bg-rose-50 text-rose-700"}`}
            data-testid={`${testid}-chip-${i}`}
            title={ok ? v : "Invalid email"}
          >
            {v}
            {!disabled && (
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-slate-200"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                aria-label={`Remove ${v}`}
                data-testid={`${testid}-chip-remove-${i}`}
              >
                <XIcon className="h-3 w-3" />
              </button>
            )}
          </span>
        );
      })}
      <input
        ref={inputRef}
        value={draftText}
        disabled={disabled}
        onChange={(e) => setDraftText(e.target.value)}
        onKeyDown={(e) => {
          if (["Enter", ",", ";"].includes(e.key)) {
            e.preventDefault();
            if (draftText.trim()) commit(draftText);
          } else if (e.key === "Backspace" && !draftText && value.length) {
            removeAt(value.length - 1);
          }
        }}
        onBlur={() => draftText.trim() && commit(draftText)}
        onPaste={(e) => {
          const t = e.clipboardData.getData("text");
          if (/[\s,;]/.test(t)) { e.preventDefault(); commit(t); }
        }}
        placeholder={value.length ? "" : placeholder}
        className="min-w-[120px] flex-1 border-0 bg-transparent p-1 text-sm outline-none disabled:cursor-not-allowed"
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// SendByEmailDialog — preview → confirm → SMTP.
// ---------------------------------------------------------------------------
function SendByEmailDialog({ claim, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);

  const [to, setTo] = useState([]);          // editable
  const [cc, setCc] = useState([]);          // editable
  const [bccReadonly, setBccReadonly] = useState([]);
  const [initialTo, setInitialTo] = useState([]);
  const [initialCcLen, setInitialCcLen] = useState(0);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendBlocking, setSendBlocking] = useState(false); // 409 → keep disabled

  const refetchTimer = useRef(null);

  // Fetch preview. When first opened, prime `to` from response; on
  // subsequent refetches (after the user edited chips), keep the current
  // values but refresh subject/body.
  const fetchPreview = useCallback(async (opts = {}) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const body = {};
      if (opts.to_override && opts.to_override.length) body.to_override = opts.to_override;
      if (opts.extra_cc && opts.extra_cc.length) body.extra_cc = opts.extra_cc;
      const { data } = await api.post(
        `/shop-credits/${claim.id}/preview-email`, body);
      setPreview(data);
      setBccReadonly(data.bcc || []);
      if (opts.firstLoad) {
        setTo(Array.isArray(data.to) ? data.to.slice() : []);
        setCc(Array.isArray(data.cc) ? data.cc.slice() : []);
        setInitialTo(Array.isArray(data.to) ? data.to.slice() : []);
        setInitialCcLen((data.cc || []).length);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setPreviewError(typeof detail === "string" ? detail
                       : (detail?.message || extractError(e)));
    } finally {
      setPreviewLoading(false);
    }
  }, [claim.id]);

  useEffect(() => {
    fetchPreview({ firstLoad: true });
    return () => clearTimeout(refetchTimer.current);
  }, [fetchPreview]);

  // Debounced refetch when the user edits To/CC chips. 400 ms is enough
  // to feel snappy but not spam the backend when pasting a list.
  useEffect(() => {
    if (previewLoading || sending) return;
    clearTimeout(refetchTimer.current);
    refetchTimer.current = setTimeout(() => {
      fetchPreview({
        to_override: toChangedFromInitial(to, initialTo) ? to.filter(isValidEmail) : undefined,
        extra_cc: cc.filter(isValidEmail),
      });
    }, 400);
    return () => clearTimeout(refetchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to.join("|"), cc.join("|")]);

  const allValid = to.length > 0 && to.every(isValidEmail) && cc.every(isValidEmail);
  const canSubmit = !!preview && allValid && !sending && !sendBlocking;

  const send = async () => {
    setSending(true);
    setSendError(null);
    try {
      const body = { confirm: true };
      if (toChangedFromInitial(to, initialTo)) body.to_override = to;
      if (cc.length) body.extra_cc = cc;
      const { data } = await api.post(`/shop-credits/${claim.id}/send`, body);
      const first = (data?.send_event?.to || to)[0] || "recipient";
      const extras = Math.max(0, (data?.send_event?.to || to).length - 1);
      toast.success(
        `Email sent to ${first}${extras ? ` +${extras} others` : ""}` +
        (data?.send_event?.redirected_by_safe_mode ? " (SAFE MODE)" : ""),
      );
      onSent?.();
      onClose();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : (detail?.message || detail?.error || extractError(e));
      if (status === 409) {
        setSendError(msg || "Claim cannot be sent in its current state.");
        setSendBlocking(true);
      } else if (status === 502) {
        setSendError(`SMTP delivery failed — ${msg}`);
      } else if (status === 400) {
        setSendError(msg || "Request rejected. Check recipients.");
      } else {
        setSendError(msg || "Send failed.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !sending) onClose(); }}>
      <DialogContent
        className="max-w-3xl"
        data-testid="sc-send-email-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            Send shop credit — <span className="font-mono text-base">{claim.claim_reference}</span>
          </DialogTitle>
          <DialogDescription>
            Preview the outbound email, adjust recipients, then confirm to
            send. The claim PDF is attached automatically.
          </DialogDescription>
        </DialogHeader>

        {previewLoading && !preview ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
          </div>
        ) : previewError ? (
          <Alert variant="destructive" data-testid="sc-send-preview-error">
            <AlertDescription>{previewError}</AlertDescription>
          </Alert>
        ) : preview && (
          <div className="space-y-3">
            {preview.safe_mode && (
              <Alert
                className="border-amber-200 bg-amber-50 text-amber-800"
                data-testid="sc-send-safe-mode-banner"
              >
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  <strong>Safe mode is ON</strong> — this email will be
                  redirected to the QA inbox, not the shop. Turn off
                  <code className="mx-1 rounded bg-amber-100 px-1 text-[11px]">SMTP_SAFE_MODE</code>
                  on the backend to reach real recipients.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 gap-3 text-sm">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">To *</label>
                <EmailChipsInput
                  value={to} onChange={setTo}
                  placeholder="Add recipient email…"
                  disabled={sending || sendBlocking}
                  testid="sc-send-to"
                />
                {to.length === 0 && (
                  <div className="mt-1 text-[11px] text-rose-600">At least one recipient is required.</div>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">CC</label>
                <EmailChipsInput
                  value={cc} onChange={setCc}
                  placeholder="Add cc…"
                  disabled={sending || sendBlocking}
                  testid="sc-send-cc"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Archive (auto)</label>
                <div
                  className="flex min-h-[38px] flex-wrap items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600"
                  data-testid="sc-send-bcc"
                >
                  {bccReadonly.length === 0
                    ? <span className="text-slate-400">— none —</span>
                    : bccReadonly.map((b) => (
                        <span key={b} className="rounded-full bg-slate-200 px-2 py-0.5">{b}</span>
                      ))}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Subject</label>
                <Input value={preview.subject || ""} disabled data-testid="sc-send-subject" />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Body preview</label>
                <Tabs defaultValue="text" className="w-full">
                  <TabsList>
                    <TabsTrigger value="text" data-testid="sc-send-tab-text">Plain text</TabsTrigger>
                    <TabsTrigger value="html" data-testid="sc-send-tab-html">HTML</TabsTrigger>
                  </TabsList>
                  <TabsContent value="text" className="mt-2">
                    <pre
                      className="max-h-[260px] overflow-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-[12px] leading-relaxed text-slate-800"
                      data-testid="sc-send-preview-text"
                    >
                      {preview.text_body || ""}
                    </pre>
                  </TabsContent>
                  <TabsContent value="html" className="mt-2">
                    <iframe
                      title="Email HTML preview"
                      srcDoc={preview.html_body || ""}
                      sandbox=""
                      className="h-[260px] w-full rounded-md border border-slate-200 bg-white"
                      data-testid="sc-send-preview-html"
                    />
                  </TabsContent>
                </Tabs>
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                <span className="mr-2 inline-flex items-center gap-1 font-medium">
                  <FileText className="h-3.5 w-3.5" /> Attachment
                </span>
                <span data-testid="sc-send-attachment">
                  {preview.attachment_filename} · {fmtBytes(preview.attachment_bytes)}
                </span>
              </div>

              {sendError && (
                <Alert variant="destructive" data-testid="sc-send-error">
                  <AlertDescription>{sendError}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={sending}
            data-testid="sc-send-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={!canSubmit}
            data-testid="sc-send-confirm"
          >
            {sending
              ? (<><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Sending…</>)
              : (<><Send className="mr-1 h-4 w-4" /> Send Email</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toChangedFromInitial(current, initial) {
  if (current.length !== initial.length) return true;
  for (let i = 0; i < current.length; i += 1) {
    if (current[i] !== initial[i]) return true;
  }
  return false;
}


// ---------------------------------------------------------------------------
// SendHistorySection — collapsible list of send_events on the drawer.
// ---------------------------------------------------------------------------
function SendHistorySection({ events }) {
  const [open, setOpen] = useState(true);
  const sorted = useMemo(
    () => (events || []).slice().sort(
      (a, b) => String(b.sent_at || "").localeCompare(String(a.sent_at || ""))
    ),
    [events],
  );
  return (
    <section data-testid="sc-send-history">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Send history ({sorted.length})
      </button>
      {open && (
        <div className="rounded-md border border-slate-200 bg-white">
          {sorted.map((ev, i) => <SendHistoryRow key={ev.event_id || i} ev={ev} />)}
        </div>
      )}
    </section>
  );
}

function SendHistoryRow({ ev }) {
  const [expanded, setExpanded] = useState(false);
  const to = Array.isArray(ev.to) ? ev.to : [];
  const first = to[0] || "—";
  const extras = Math.max(0, to.length - 1);
  return (
    <div className="border-b border-slate-100 px-3 py-2 text-sm last:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-medium text-slate-800">{fmtDT(ev.sent_at)}</span>
          <span className="text-xs text-slate-500">by {ev.sent_by?.email || "—"}</span>
          {ev.is_first_send && (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">first send</Badge>
          )}
          {ev.redirected_by_safe_mode && (
            <Badge
              className="bg-amber-100 text-amber-800 hover:bg-amber-100"
              title="Safe mode was ON — email went to the QA inbox instead of the recipients below."
            >
              Safe Mode
            </Badge>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          {expanded ? "Hide details" : "Details"}
        </button>
      </div>
      <div className="mt-0.5 text-xs text-slate-600">
        to: <span className="font-medium text-slate-800">{first}</span>
        {extras > 0 && <span> +{extras} more</span>}
      </div>
      {expanded && (
        <div className="mt-2 space-y-1 rounded bg-slate-50 p-2 text-[11px] text-slate-700">
          <div><span className="text-slate-500">To:</span> {to.join(", ") || "—"}</div>
          <div><span className="text-slate-500">CC:</span> {(ev.cc || []).join(", ") || "—"}</div>
          <div><span className="text-slate-500">BCC:</span> {(ev.bcc || []).join(", ") || "—"}</div>
          {ev.effective_to && (ev.effective_to || []).join("") !== to.join("") && (
            <div>
              <span className="text-slate-500">Effective envelope:</span>{" "}
              {(ev.effective_to || []).join(", ") || "—"}
            </div>
          )}
          {ev.subject && (
            <div className="truncate"><span className="text-slate-500">Subject:</span> {ev.subject}</div>
          )}
          {ev.message_id && (
            <div className="truncate font-mono">
              <span className="text-slate-500">Message-ID:</span> {ev.message_id}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
